import { trace } from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-proto';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_DEPLOYMENT_ENVIRONMENT_NAME, ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';

const TRACER_NAME = 'savaf-frontend';

let provider: WebTracerProvider | null = null;

export function initTracing() {
  const otlpUrl = import.meta.env.VITE_OPENOBSERVE_OTLP_URL;
  if (!otlpUrl) return;

  const environment = import.meta.env.VITE_APP_ENV || (import.meta.env.DEV ? 'development' : 'production');

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: TRACER_NAME,
    [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: environment,
  });

  const otlpToken = import.meta.env.VITE_OPENOBSERVE_OTLP_TOKEN;
  const headers: Record<string, string> = otlpToken ? { Authorization: `Basic ${otlpToken}` } : {};

  const exporter = new OTLPTraceExporter({
    url: otlpUrl,
    headers,
  });

  provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(exporter, {
        maxQueueSize: 100,
        maxExportBatchSize: 10,
        scheduledDelayMillis: 5000,
      }),
    ],
  });

  provider.register();

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const rumSite = import.meta.env.VITE_OPENOBSERVE_RUM_SITE;

  const ignoreUrls: RegExp[] = [new RegExp(otlpUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))];
  if (rumSite) {
    ignoreUrls.push(new RegExp(rumSite.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')));
  }

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [new RegExp(supabaseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))],
        ignoreUrls,
      }),
      new DocumentLoadInstrumentation(),
    ],
  });
}

export function getTracer() {
  return trace.getTracer(TRACER_NAME);
}

export function traceSpan<T>(name: string, fn: () => T): T {
  const tracer = getTracer();
  return tracer.startActiveSpan(name, span => {
    try {
      const result = fn();
      if (result instanceof Promise) {
        return result
          .then(v => {
            span.end();
            return v;
          })
          .catch(err => {
            span.recordException(err);
            span.end();
            throw err;
          }) as T;
      }
      span.end();
      return result;
    } catch (err) {
      span.recordException(err as Error);
      span.end();
      throw err;
    }
  });
}

export async function flushTraces() {
  if (provider) {
    await provider.forceFlush();
  }
}
